import React, {useState} from 'react'
import {Formik, Form} from 'formik'
import {ReportFilter} from '../types'
import {format, startOfMonth, endOfMonth, subDays, startOfYear} from 'date-fns'

interface ReportFiltersProps {
  filters: ReportFilter[]
  values: Record<string, any>
  onChange: (values: Record<string, any>) => void
  onApply: () => void
}

const DATE_PRESETS: Record<string, {from: Date; to: Date; label: string}> = {
  LAST_7_DAYS: {
    from: subDays(new Date(), 6),
    to: new Date(),
    label: 'Last 7 Days',
  },
  LAST_30_DAYS: {
    from: subDays(new Date(), 29),
    to: new Date(),
    label: 'Last 30 Days',
  },
  THIS_MONTH: {
    from: startOfMonth(new Date()),
    to: new Date(),
    label: 'This Month',
  },
  LAST_MONTH: {
    from: startOfMonth(subDays(startOfMonth(new Date()), 1)),
    to: subDays(startOfMonth(new Date()), 1),
    label: 'Last Month',
  },
  THIS_YEAR: {
    from: startOfYear(new Date()),
    to: new Date(),
    label: 'This Year',
  },
}

const formatDate = (d: Date) => format(d, 'yyyy-MM-dd')

const ReportFilters: React.FC<ReportFiltersProps> = ({filters, values, onChange, onApply}) => {
  const [expanded, setExpanded] = useState(true)

  const renderFilter = (filter: ReportFilter, formik: any) => {
    const filterType = filter.type?.toLowerCase()
    switch (filterType) {
      case 'text':
        return (
          <input
            type='text'
            className='form-control form-control-solid'
            placeholder={filter.placeholder || `Enter ${filter.label}`}
            value={formik.values[filter.field] || ''}
            onChange={(e) => formik.setFieldValue(filter.field, e.target.value)}
          />
        )

      case 'number':
        return (
          <input
            type='number'
            className='form-control form-control-solid'
            placeholder={filter.placeholder || `Enter ${filter.label}`}
            value={formik.values[filter.field] || ''}
            onChange={(e) => formik.setFieldValue(filter.field, e.target.value)}
          />
        )

      case 'date':
        return (
          <input
            type='date'
            className='form-control form-control-solid'
            value={formik.values[filter.field] || ''}
            onChange={(e) => formik.setFieldValue(filter.field, e.target.value)}
          />
        )

      case 'date_range':
        const dateValue = formik.values[filter.field] || {}
        return (
          <div>
            <div className='d-flex flex-wrap gap-2 mb-3'>
              {Object.entries(DATE_PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  type='button'
                  className={`btn btn-sm ${
                    dateValue.preset === key ? 'btn-primary' : 'btn-light'
                  }`}
                  onClick={() =>
                    formik.setFieldValue(filter.field, {
                      from: formatDate(preset.from),
                      to: formatDate(preset.to),
                      preset: key,
                    })
                  }
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className='d-flex gap-3 align-items-center'>
              <input
                type='date'
                className='form-control form-control-solid'
                value={dateValue.from || ''}
                onChange={(e) =>
                  formik.setFieldValue(filter.field, {
                    ...dateValue,
                    from: e.target.value,
                    preset: 'CUSTOM',
                  })
                }
              />
              <span className='text-gray-500 fw-semibold'>to</span>
              <input
                type='date'
                className='form-control form-control-solid'
                value={dateValue.to || ''}
                onChange={(e) =>
                  formik.setFieldValue(filter.field, {
                    ...dateValue,
                    to: e.target.value,
                    preset: 'CUSTOM',
                  })
                }
              />
            </div>
          </div>
        )

      case 'select':
        return (
          <select
            className='form-select form-select-solid'
            value={formik.values[filter.field] || ''}
            onChange={(e) => formik.setFieldValue(filter.field, e.target.value)}
          >
            <option value=''>Select {filter.label}</option>
            {filter.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )

      case 'multi_select':
        return (
          <select
            className='form-select form-select-solid'
            multiple
            value={formik.values[filter.field] || []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, (opt) => opt.value)
              formik.setFieldValue(filter.field, selected)
            }}
          >
            {filter.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )

      case 'number_range':
        const rangeValue = formik.values[filter.field] || {}
        return (
          <div className='d-flex gap-3 align-items-center'>
            <input
              type='number'
              className='form-control form-control-solid'
              placeholder='Min'
              value={rangeValue.from || ''}
              onChange={(e) =>
                formik.setFieldValue(filter.field, {
                  ...rangeValue,
                  from: e.target.value,
                })
              }
            />
            <span className='text-gray-500 fw-semibold'>to</span>
            <input
              type='number'
              className='form-control form-control-solid'
              placeholder='Max'
              value={rangeValue.to || ''}
              onChange={(e) =>
                formik.setFieldValue(filter.field, {
                  ...rangeValue,
                  to: e.target.value,
                })
              }
            />
          </div>
        )

      default:
        return null
    }
  }

  const getInitialValues = () => {
    const initial: Record<string, any> = {...values}
    filters.forEach((filter) => {
      const filterType = filter.type?.toLowerCase()
      const currentValue = initial[filter.field]

      // Resolve date_range presets: convert string/object presets to {from, to, preset}
      // Backend sends defaultValue as object {name: "THIS_MONTH", displayName: "This Month"} due to @JsonFormat(shape=OBJECT)
      if (filterType === 'date_range') {
        const needsResolution =
          currentValue === undefined ||
          typeof currentValue === 'string' ||
          (typeof currentValue === 'object' && currentValue !== null && !currentValue.from)
        if (needsResolution) {
          let presetName = 'THIS_MONTH'
          if (typeof currentValue === 'string') {
            presetName = currentValue
          } else if (typeof currentValue === 'object' && currentValue?.name) {
            presetName = currentValue.name
          } else if (typeof filter.defaultValue === 'string') {
            presetName = filter.defaultValue
          } else if (filter.defaultValue?.name) {
            presetName = filter.defaultValue.name
          }
          const preset = DATE_PRESETS[presetName]
          if (preset) {
            initial[filter.field] = {
              from: formatDate(preset.from),
              to: formatDate(preset.to),
              preset: presetName,
            }
          } else {
            initial[filter.field] = {
              from: formatDate(startOfMonth(new Date())),
              to: formatDate(endOfMonth(new Date())),
              preset: 'THIS_MONTH',
            }
          }
        }
      } else if (currentValue === undefined && filter.defaultValue !== undefined) {
        initial[filter.field] = filter.defaultValue
      }
    })
    return initial
  }

  return (
    <div className='mb-5'>
      <div
        className='d-flex align-items-center cursor-pointer py-3 px-4 rounded bg-light-primary mb-4'
        onClick={() => setExpanded(!expanded)}
        style={{transition: 'all 0.2s ease'}}
      >
        <i className='bi bi-funnel-fill text-primary me-3 fs-4'></i>
        <span className='fw-bold fs-5 text-primary'>Filters</span>
        <i className={`bi bi-chevron-${expanded ? 'up' : 'down'} ms-auto text-primary fs-5`}></i>
      </div>

      {expanded && (
        <Formik initialValues={getInitialValues()} enableReinitialize onSubmit={(formValues) => {
          onChange(formValues)
          onApply()
        }}>
          {(formik) => (
            <Form>
              <div className='rounded p-5' style={{backgroundColor: 'var(--bs-gray-100)'}}>
                <div className='row g-4'>
                  {filters.map((filter) => (
                    <div key={filter.field} className='col-12'>
                      <label className='form-label fw-bold text-gray-700 fs-6 mb-2'>
                        <i className='bi bi-calendar-range me-2 text-gray-500'></i>
                        {filter.label}
                        {filter.required && <span className='text-danger ms-1'>*</span>}
                      </label>
                      {renderFilter(filter, formik)}
                    </div>
                  ))}
                </div>

                <div className='d-flex justify-content-end gap-3 mt-5 pt-4 border-top border-gray-300'>
                  <button
                    type='button'
                    className='btn btn-light-danger btn-sm px-4'
                    onClick={() => {
                      formik.resetForm()
                      onChange({})
                    }}
                  >
                    <i className='bi bi-x-circle me-2'></i>
                    Clear Filters
                  </button>
                  <button type='submit' className='btn btn-primary btn-sm px-4'>
                    <i className='bi bi-search me-2'></i>
                    Apply Filters
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  )
}

export default ReportFilters
